// Kişinin duyuru akışı (/api/announcements/feed): sadece kendisini hedefleyen aktif
// duyurular + kendi okundu durumu. Ana sayfa kartı ve panel şeridi kullanır.
// enabled=false iken hiç istek atılmaz (giriş öncesi ekranda duyuru yok).
// "Gördü": duyurunun başlığı ekranda gösterildiğinde (markSeen) — bir kez gönderilir.
//
// Ne zaman yenilenir: açılınca, oturum değişince ("auth-changed": giriş, token
// yenileme), 2 dk'da bir, sekme görünür olunca, onay sonrası. İstek başarısız olursa
// 2 dk beklemeden kısa aralıklarla tekrar denenir (önemli duyurunun asıl güvencesi
// şerit — boş kalmamalı). Kullanıcı değiştiyse eski kullanıcının yanıtı atılır.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { getSession } from "../../lib/auth";
import { FEED_REFRESH_MS, FEED_CHANGED_EVENT, FEED_RETRY_MS } from "../../lib/announcements";

const sessionUser = () => {
  const s = getSession();
  return s ? String(s.user_id || s.username || "") : "";
};

export default function useAnnouncementFeed(enabled) {
  const [items, setItems] = useState(null);
  const seenSent = useRef(new Set());
  const reqId = useRef(0);
  const retry = useRef({ n: 0, timer: null });
  const userRef = useRef(sessionUser());

  const clearRetry = () => { clearTimeout(retry.current.timer); retry.current.timer = null; };

  const load = useCallback(async () => {
    if (!enabled) return;
    const id = ++reqId.current;
    const user = sessionUser();
    clearRetry();
    try {
      const r = await axios.get(`${API}/announcements/feed`);
      // daha yeni bir istek başladıysa ya da bu arada kullanıcı değiştiyse yanıtı at
      if (id !== reqId.current || user !== sessionUser()) return;
      retry.current.n = 0;
      setItems(r.data);
    } catch (_) {
      if (id !== reqId.current) return;
      // son veri ekranda kalsın; kısa aralıkla tekrar dene (3 sn, 10 sn, 30 sn, sonra 30 sn)
      const n = retry.current.n;
      retry.current.n = n + 1;
      retry.current.timer = setTimeout(() => load(), FEED_RETRY_MS[Math.min(n, FEED_RETRY_MS.length - 1)]);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      reqId.current += 1; clearRetry(); retry.current.n = 0;
      setItems(null); seenSent.current = new Set();
      return undefined;
    }
    load();
    const t = setInterval(load, FEED_REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    // Oturum değişti: giriş / token yenileme → hemen yenile; kullanıcı değiştiyse
    // önce eski kullanıcının duyurularını ekrandan kaldır. Çıkışta enabled zaten kapanır.
    const onAuth = (e) => {
      if (e?.detail?.type === "logout") return;
      const u = sessionUser();
      if (u !== userRef.current) {
        userRef.current = u;
        setItems(null); seenSent.current = new Set();
      }
      load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(FEED_CHANGED_EVENT, load);
    window.addEventListener("auth-changed", onAuth);
    return () => {
      clearInterval(t); clearRetry();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(FEED_CHANGED_EVENT, load);
      window.removeEventListener("auth-changed", onAuth);
    };
  }, [enabled, load]);

  const patchMe = (id, me) => setItems((xs) => (xs || []).map((a) => (a.id === id ? { ...a, me } : a)));

  const markSeen = useCallback((list) => {
    (list || []).forEach((a) => {
      if (a.me?.seen_at || seenSent.current.has(a.id)) return;
      seenSent.current.add(a.id);
      axios.post(`${API}/announcements/${a.id}/seen`)
        .then((r) => patchMe(a.id, r.data))
        .catch(() => { seenSent.current.delete(a.id); });
    });
  }, []);

  // Okudum: hata olursa fırlatır (çağıran mesaj gösterir)
  const ack = useCallback(async (id) => {
    const r = await axios.post(`${API}/announcements/${id}/ack`);
    patchMe(id, r.data);
    window.dispatchEvent(new Event(FEED_CHANGED_EVENT));
  }, []);

  return { items, load, markSeen, ack };
}
